/**
 * DecisionCard — the ONE decision surface both hosts render.
 *
 * emuw-v1-5 `shared-decision-components` Phase 3 task 9.
 *
 * It is the union of the two hosts' existing compositions, which were already the
 * same two components in the same order — the add-in's `ReviewCheckpointPanel` and
 * Ora's non-bulk drawer branch. What differed was the TRANSPORT, so the transports
 * are injected and the surface is shared. Neither host gets a variant.
 *
 * THINGS THIS CARD DELIBERATELY DOES NOT DO, each of which would show up as a
 * visible change in the Outlook pane — which AC-6 forbids:
 *
 *   * It renders NO error alert of its own. Both hosts pass the message DOWN into
 *     `HitlQuestionPanel`, which is what renders it. A card that rendered its own
 *     alert AND forwarded `error` would double the message in BOTH hosts.
 *   * No `DetailSection` wrapper and no heading. `HitlDrawerBody`'s outer
 *     `Collapse` owns the slide-in chrome and the question prompt is the implicit
 *     heading.
 *   * No width, breakpoint, viewport or two-column rule ANYWHERE. The operator
 *     reversed that design on 2026-08-25, and a media query here would reach the
 *     pane through the shared module.
 *
 * It keeps `data-testid="review-checkpoint-panel"` alongside the new
 * `decision-card`, so the add-in's existing suites stay green — that is AC-6's
 * machine-checkable half.
 *
 * WHEN THE DECISION IS ANSWERED, THE OPTION CONTROLS ARE NOT RENDERED AT ALL —
 * not rendered-disabled. A disabled-looking card still reads as answerable, and
 * that stale card is the thing this story exists to remove.
 */

import React, { useEffect, useRef, type ReactNode } from "react";

import { logDecisionRefusal } from "../decisionRefusalLog";
import { hitlQuestionKey } from "../hitlQuestionKey";
import { useAnsweredDecisionSummary } from "../useAnsweredDecisionSummary";
import { DecisionAnsweredCard } from "./DecisionAnsweredCard";
import { HitlQuestionPanel } from "./HitlQuestionPanel";
import type { ConfidenceLevel } from "./JsonSchemaForm";
import HitlSideActions, { type SideActionResult } from "./HitlSideActions";
import type { HitlEvent } from "./WorkflowHitlHistorySection";

export interface DecisionCardProps {
  hitlQuestion: Record<string, unknown> | null;
  onSubmit: (payload: Record<string, unknown>) => void;
  isSubmitting: boolean;
  error: string | null;
  /**
   * From `useHitlSubmitLatch`. Set ONLY when THIS surface's own submit was
   * refused. When set the card shows the answered state and offers no answer.
   */
  refusal: { reason: "already-answered"; detail: string | null } | null;
  /**
   * From `useAnsweredElsewhere`. Set when the decision this surface was showing
   * was answered on the OTHER surface — nobody here clicked anything.
   */
  answeredElsewhere?: boolean;
  /**
   * Per-field extraction confidence for the values under review, keyed by
   * top-level schema property. Supplied by the host from the analyze_email
   * prefill result it already holds; forwarded to the schema form so the
   * Servicer can see how much to trust each prefilled value (AC 3).
   *
   * Optional: a workflow with no prefill simply renders no chips.
   */
  fieldConfidence?: Record<string, ConfidenceLevel>;
  instanceId: string | null;
  fetchHitlHistory: (instanceId: string) => Promise<HitlEvent[]>;
  /**
   * Which surface is mounting this. The only thing the card cannot infer, and the
   * only reason it is a prop — the refusal log line records it.
   */
  host: "outlook-addin" | "ora";
  onSideAction: (endpoint: string) => Promise<SideActionResult>;
  /**
   * Lets Ora supply its bulk-review branch WITHOUT a forked card. Rendered in
   * place of the question panel when the gate declares a renderer this card does
   * not own.
   */
  rendererOverride?: ReactNode | null;
  /**
   * Href of the page that owns the work behind this gate, rendered by the
   * question panel as the primary affordance above the decision pills. Only
   * ``type: "report_review"`` uses it today (the quote-compare editor).
   * Optional and passed straight through: the Outlook add-in has no ora router
   * and a different origin, so it omits it and the gate renders unchanged.
   */
  reviewHref?: string;
}

export function DecisionCard({
  hitlQuestion,
  onSubmit,
  isSubmitting,
  error,
  refusal,
  answeredElsewhere = false,
  fieldConfidence,
  instanceId,
  fetchHitlHistory,
  host,
  onSideAction,
  rendererOverride = null,
  reviewHref,
}: DecisionCardProps): React.ReactElement | null {
  const isAnswered = refusal != null || answeredElsewhere;

  // The key of the question that was last on screen. Captured while it is still
  // there: by the time a refusal lands, `hitlQuestion` is usually already null.
  const lastQuestionKeyRef = useRef<string | null>(null);
  if (hitlQuestion != null) lastQuestionKeyRef.current = hitlQuestionKey(hitlQuestion);

  // The card resolves the recorded answer itself, so neither host derives it and
  // neither host can drift. Fires only on the resolved transition.
  const { answeredWith } = useAnsweredDecisionSummary({
    instanceId,
    hitlQuestion,
    enabled: isAnswered,
    fetchHitlHistory,
  });

  // DoD Monitoring: record the refused second answer. Keyed on the refusal's
  // question so it fires ONCE per refusal, not once per render — and only on the
  // refusal path, never on accepted or answered-elsewhere, which are not
  // staleness events.
  const loggedForRef = useRef<string | null>(null);
  const refusedKey =
    refusal != null
      ? `${instanceId ?? "?"}:${lastQuestionKeyRef.current ?? "unknown"}`
      : null;
  useEffect(() => {
    if (!refusedKey || loggedForRef.current === refusedKey) return;
    loggedForRef.current = refusedKey;
    logDecisionRefusal({
      instanceId,
      stepId:
        typeof hitlQuestion?.step_id === "string" ? hitlQuestion.step_id : null,
      questionKey: lastQuestionKeyRef.current,
      host,
    });
    // `hitlQuestion` is deliberately absent from the deps: on the refusal
    // transition it is usually already null, and adding it would re-fire the log
    // on the frame it clears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refusedKey, instanceId, host]);

  // Nothing to show: no question and nothing answered.
  if (!hitlQuestion && !isAnswered) return null;

  return (
    <div data-testid="decision-card" data-host={host}>
      {/* The add-in's existing suites select this id. Kept reachable on purpose —
          losing it would break the pane's tests, which is what AC-6 forbids. */}
      <div data-testid="review-checkpoint-panel">
        {isAnswered ? (
          <DecisionAnsweredCard
            answeredWith={answeredWith}
            // `refused` distinguishes "your click did not land" from "somebody
            // else answered this". Both render the same card; only the wording
            // differs, and only one of them is about something the AM did.
            refused={refusal != null}
          />
        ) : (
          <>
            {rendererOverride ?? (
              <HitlQuestionPanel
                hitlQuestion={hitlQuestion as Record<string, unknown>}
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
                // Passed DOWN, never rendered here — see the header note.
                error={error}
                fieldConfidence={fieldConfidence}
                reviewHref={reviewHref}
              />
            )}
            {hitlQuestion && (
              <HitlSideActions
                hitlQuestion={hitlQuestion as Record<string, unknown>}
                onAction={onSideAction}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DecisionCard;
